import axios from "axios";
import { api, apiUrl } from "../../../shared/services/service";
import type {
  VoyageEvaluationDashboard,
  VoyagePerformanceReportsResponse,
} from "../models/voyage-evaluation.models";

// Loads the complete allowed landing-page window so its recovered slicers can react immediately.
export const getVoyageEvaluationDashboard = async (
  signal?: AbortSignal,
): Promise<VoyageEvaluationDashboard> => {
  const firstPage = await getVoyageEvaluationDashboardPage(0, signal);
  const voyages = [...firstPage.voyages];

  while (voyages.length < firstPage.total) {
    const nextPage = await getVoyageEvaluationDashboardPage(voyages.length, signal);

    if (nextPage.voyages.length === 0) {
      throw new Error("Voyage evaluation pagination ended before every voyage was loaded.");
    }

    voyages.push(...nextPage.voyages);
  }

  return { ...firstPage, voyages, offset: 0, limit: voyages.length };
};

const getVoyageEvaluationDashboardPage = async (
  offset: number,
  signal?: AbortSignal,
): Promise<VoyageEvaluationDashboard> => {
  const response = await api.get<VoyageEvaluationDashboard>(
    `${apiUrl}/pbi/voyage-evaluation/dashboard`,
    { params: { offset, limit: 500 }, signal },
  );

  return response.data;
};

// Loads one selected voyage's governed report rows; view-only slicers are applied locally.
export const getVoyagePerformanceReports = async (
  imo: string,
  voyage: string,
  signal?: AbortSignal,
): Promise<VoyagePerformanceReportsResponse> => {
  const firstPage = await getVoyagePerformanceReportsPage(imo, voyage, 0, signal);
  const reports = [...firstPage.reports];

  while (reports.length < firstPage.total) {
    const nextPage = await getVoyagePerformanceReportsPage(imo, voyage, reports.length, signal);

    if (nextPage.reports.length === 0) {
      throw new Error("Voyage report pagination ended before every report was loaded.");
    }

    reports.push(...nextPage.reports);
  }

  return { ...firstPage, reports, offset: 0, limit: reports.length };
};

const getVoyagePerformanceReportsPage = async (
  imo: string,
  voyage: string,
  offset: number,
  signal?: AbortSignal,
): Promise<VoyagePerformanceReportsResponse> => {
  const response = await api.get<VoyagePerformanceReportsResponse>(
    `${apiUrl}/pbi/voyage-evaluation/performance-reports`,
    { params: { imo, voyage, offset, limit: 500 }, signal },
  );

  return response.data;
};

export const getVoyageEvaluationErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const response = error.response?.data as { error?: string; message?: string } | undefined;

    return response?.error ?? response?.message ?? error.message;
  }

  return error instanceof Error ? error.message : "Voyage evaluation data could not be loaded.";
};
